import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Image, Trophy, CheckCircle, XCircle, Star, Filter } from 'lucide-react';

const WARDS = ['ward-1','ward-2','ward-3','ward-4','ward-5','ward-6','ward-7','ward-8','ward-9','ward-10'];
const SLOTS = ['primary', 'secondary', 'map'];
const STATUS_COLORS = {
  pending:  'text-yellow-400 bg-yellow-900/20 border-yellow-800',
  approved: 'text-green-400 bg-green-900/20 border-green-800',
  rejected: 'text-red-400 bg-red-900/20 border-red-800',
  winner:   'text-[#C5A52A] bg-yellow-900/30 border-yellow-600',
};

const WardPhotosAdminTab = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wardFilter, setWardFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => { fetchPhotos(); }, [wardFilter, statusFilter]);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (wardFilter) params.set('ward', wardFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/admin/ward-photos?${params}`);
      setPhotos(res.data?.photos || []);
    } catch { toast.error('Failed to load photos'); }
    finally { setLoading(false); }
  };

  const handleAction = async (id, action, orgName) => {
    const labels = { approve: 'Approve', reject: 'Reject', winner: 'Select as WINNER' };
    if (!window.confirm(`${labels[action]} this photo submission?`)) return;
    setActionLoading(id);
    try {
      await api.patch(`/admin/ward-photos/${id}`, null, { params: { action } });
      if (action === 'winner') toast.success('Winner selected! Ward bento image updated + 50 pts awarded.');
      else toast.success(`Photo ${action}d`);
      fetchPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setActionLoading(null); }
  };

  const pendingCount = photos.filter(p => p.status === 'pending').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Image className="w-6 h-6 text-[#C5A52A]" /> Ward Photo Competition
          </h2>
          <p className="text-gray-500 text-sm font-mono mt-1">
            Review community photo submissions. Selecting a winner updates the live ward page image and awards 50 points.
          </p>
        </div>
        {pendingCount > 0 && (
          <span className="bg-yellow-600 text-black text-xs font-bold px-3 py-1 rounded-full animate-pulse">
            {pendingCount} PENDING
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-gray-500" />
          <select value={wardFilter} onChange={e => setWardFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white text-xs px-2 py-1.5 rounded font-mono">
            <option value="">All Wards</option>
            {WARDS.map(w => <option key={w} value={w}>{w.replace('-', ' ').toUpperCase()}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white text-xs px-2 py-1.5 rounded font-mono">
            <option value="">All Status</option>
            {['pending','approved','rejected','winner'].map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm font-mono text-center py-12">Loading submissions...</p>
      ) : photos.length === 0 ? (
        <div className="border-2 border-dashed border-gray-800 rounded p-12 text-center">
          <Image className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 font-mono text-sm">No photo submissions yet.</p>
          <p className="text-gray-600 font-mono text-xs mt-1">Remember to run <code className="text-yellow-600">/app/memory/ward_photos_table_setup.sql</code> in Supabase.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {photos.map(photo => (
            <div key={photo.id} className={`bg-gray-900 border rounded overflow-hidden ${photo.status === 'winner' ? 'border-yellow-600' : 'border-gray-800'}`}>
              {/* Image preview */}
              <div className="relative w-full h-44 bg-gray-800">
                <img src={photo.image_url} alt={photo.caption || 'Submission'} className="w-full h-full object-cover" />
                <span className={`absolute top-2 right-2 text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_COLORS[photo.status]}`}>
                  {photo.status === 'winner' ? '★ WINNER' : photo.status?.toUpperCase()}
                </span>
              </div>
              {/* Info */}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-400 font-mono">{photo.ward?.replace('-', ' ').toUpperCase()}</span>
                  <span className="text-gray-600">·</span>
                  <span className="text-xs text-blue-400 font-mono">{photo.slot} slot</span>
                </div>
                <p className="text-white text-sm font-medium mb-0.5">{photo.user_name || 'Unknown'}</p>
                {photo.caption && <p className="text-gray-500 text-xs mb-2 line-clamp-2">{photo.caption}</p>}
                <p className="text-gray-700 text-[10px] font-mono mb-3">{new Date(photo.submitted_at).toLocaleString()}</p>
                {/* Action buttons */}
                {photo.status === 'pending' && (
                  <div className="flex gap-2">
                    <button onClick={() => handleAction(photo.id, 'winner')} disabled={actionLoading === photo.id}
                      className="flex-1 flex items-center justify-center gap-1 bg-[#C5A52A] hover:bg-yellow-400 text-black text-xs font-mono py-1.5 rounded disabled:opacity-50 transition-colors">
                      <Trophy className="w-3 h-3" /> Winner
                    </button>
                    <button onClick={() => handleAction(photo.id, 'approve')} disabled={actionLoading === photo.id}
                      className="flex items-center gap-1 bg-green-700 hover:bg-green-600 text-white text-xs font-mono px-2 py-1.5 rounded disabled:opacity-50 transition-colors">
                      <CheckCircle className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleAction(photo.id, 'reject')} disabled={actionLoading === photo.id}
                      className="flex items-center gap-1 bg-red-700 hover:bg-red-600 text-white text-xs font-mono px-2 py-1.5 rounded disabled:opacity-50 transition-colors">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {photo.status === 'approved' && (
                  <button onClick={() => handleAction(photo.id, 'winner')} disabled={actionLoading === photo.id}
                    className="w-full flex items-center justify-center gap-1.5 bg-[#C5A52A]/20 hover:bg-[#C5A52A]/40 border border-[#C5A52A]/40 text-[#C5A52A] text-xs font-mono py-1.5 rounded disabled:opacity-50 transition-colors">
                    <Star className="w-3 h-3" /> Select as Winner
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WardPhotosAdminTab;
